import React, { useState, useEffect, useRef } from "react";
import { Loader2, Tag, CheckCircle2, XCircle, Ticket } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function ReferralCodeField({ currency = "INR", whatsapp = "", onApplied }) {
  const [code, setCode] = useState(() => localStorage.getItem("gu_ref_customer") || "");
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [autoApplied, setAutoApplied] = useState(null);
  const waRef = useRef(whatsapp);
  waRef.current = whatsapp;
  const codeRef = useRef(code);
  codeRef.current = code;
  const statusRef = useRef(status);
  statusRef.current = status;

  const apply = async (value) => {
    const v = (value ?? code).trim().toUpperCase();
    if (!v) {
      setStatus(null);
      onApplied?.(null);
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.get("/referral/validate", { params: { code: v, whatsapp: waRef.current || "" } });
      setStatus(data);
      onApplied?.(data.valid ? { code: v, ...data } : null);
    } catch {
      setStatus({ valid: false, message: "Could not verify the code — please try again" });
      onApplied?.(null);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (code) apply(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waDigits = (whatsapp || "").replace(/\D/g, "");
  useEffect(() => {
    if (waDigits.length < 10) return;
    const t = setTimeout(() => {
      if (codeRef.current && statusRef.current) apply(codeRef.current);
      api.get("/referral/my-coupons", { params: { whatsapp: waDigits } })
        .then(({ data }) => {
          const active = (data.coupons || []).filter((c) => c.status === "active");
          const best = active.sort((a, b) => (b.amount_inr || 0) - (a.amount_inr || 0))[0] || null;
          setSuggestion(best);
          if (best && !codeRef.current && !statusRef.current) {
            setAutoApplied(best.code);
            setCode(best.code);
            apply(best.code);
          }
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waDigits]);

  const removeCoupon = () => {
    setCode("");
    setStatus(null);
    setAutoApplied(null);
    onApplied?.(null);
  };

  const discount = status?.valid
    ? (currency === "USD" && Number(status.discount_usd) > 0 ? `$${status.discount_usd}` : `₹${status.discount_inr}`)
    : null;
  const isAutoApplied = autoApplied && status?.valid && code === autoApplied;

  return (
    <div data-testid="referral-code-field">
      <label className="text-sm font-medium text-[#1A1A1A] mb-1.5 flex items-center gap-1.5">
        <Tag size={13} className="text-[var(--brand-primary)]" /> Referral / Coupon Code <span className="text-[11px] text-[var(--brand-secondary)] font-normal">(optional)</span>
      </label>
      <div className="flex gap-2">
        <Input data-testid="referral-code-input" placeholder="e.g. GUC1A2B3C" value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setStatus(null); setAutoApplied(null); onApplied?.(null); }}
          className="focus-visible:ring-[var(--brand-primary)] uppercase" />
        <Button type="button" data-testid="referral-code-apply-btn" variant="outline" onClick={() => apply()}
          disabled={checking || !code.trim()} className="shrink-0 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_92%)]">
          {checking ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
        </Button>
      </div>
      {suggestion && (!status?.valid || code !== suggestion.code) && (
        <button type="button" data-testid="referral-coupon-suggestion-btn"
          onClick={() => { setCode(suggestion.code); apply(suggestion.code); }}
          className="mt-2 flex items-center gap-1.5 text-[12px] font-semibold text-[var(--brand-primary)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_92%)] border border-[color-mix(in_oklab,var(--brand-primary),transparent_70%)] rounded-md px-3 py-1.5 hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_86%)]">
          <Ticket size={13} /> You have a coupon <code className="tracking-widest">{suggestion.code}</code> — {currency === "USD" && Number(suggestion.amount_usd) > 0 ? `$${Number(suggestion.amount_usd)}` : `₹${Number(suggestion.amount_inr).toLocaleString("en-IN")}`} off · Tap to apply
        </button>
      )}
      {status && (
        <p data-testid="referral-code-message" className={`mt-1.5 text-[12px] font-medium flex items-center gap-1 flex-wrap ${status.valid ? "text-green-700" : "text-red-600"}`}>
          {status.valid ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          {status.valid
            ? (isAutoApplied
              ? `Your coupon ${code} was applied automatically — ${discount} off at checkout`
              : `${discount} discount will be applied at checkout`)
            : status.message}
          {isAutoApplied && (
            <button type="button" data-testid="referral-coupon-remove-btn" onClick={removeCoupon}
              className="text-red-500 underline font-semibold ml-1">
              Remove
            </button>
          )}
        </p>
      )}
    </div>
  );
}
